import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Clock } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { CtaBanner } from "./Home";
import { IMG } from "../data/site";
import { ARTICLES, BLOG_CATEGORIES } from "../data/blog";

const Blog = () => {
  const [cat, setCat] = useState("All");
  const featured = ARTICLES.find((a) => a.featured) || ARTICLES[0];
  const rest = ARTICLES.filter((a) => a.slug !== featured.slug);
  const list = cat === "All" ? rest : rest.filter((a) => a.category === cat);
  const showFeatured = cat === "All";

  return (
    <div data-testid="blog-page">
      <PageHero
        eyebrow="INSIGHTS & ADVICE"
        title="Blog"
        subtitle="Practical air conditioning & heating advice for Melbourne homes and businesses."
        image={IMG.interior}
        crumbs={[{ label: "Blog" }]}
      />

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="max-w-3xl mb-14">
            <span className="text-js-blue font-semibold text-sm tracking-wide">THE J&amp;S KNOWLEDGE CENTRE</span>
            <p className="text-js-charcoal/70 text-lg leading-relaxed mt-4">
              J&amp;S Air Conditioning shares practical, no-nonsense advice on air conditioning, heating,
              ducted systems, maintenance, energy efficiency and commercial HVAC — written by an
              experienced Melbourne team to help you get the most from your system and make confident decisions.
            </p>
          </Reveal>

          {/* FEATURED */}
          {showFeatured && (
            <Reveal className="mb-16">
              <Link
                to={`/blog/${featured.slug}`}
                data-testid="blog-featured-card"
                className="group grid lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden border border-black/5 bg-js-grey hover:shadow-[0_30px_70px_rgba(0,0,0,0.12)] transition-shadow duration-500"
              >
                <div className="relative h-64 lg:h-auto overflow-hidden">
                  <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <span className="absolute top-5 left-5 px-3.5 py-1.5 rounded-full bg-js-red text-white text-xs font-semibold tracking-wide">FEATURED</span>
                </div>
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-3 text-xs mb-4">
                    <span className="px-3 py-1 rounded-full bg-js-blue/10 text-js-blue font-semibold">{featured.category}</span>
                    <span className="flex items-center gap-1.5 text-js-charcoal/50"><Clock size={13} /> {featured.readTime}</span>
                  </div>
                  <h2 className="font-display font-extrabold text-2xl md:text-3xl lg:text-4xl tracking-tight leading-tight group-hover:text-js-blue transition-colors duration-300">
                    {featured.title}
                  </h2>
                  <p className="text-js-charcoal/60 mt-5 leading-relaxed">{featured.excerpt}</p>
                  <span className="mt-7 inline-flex items-center gap-2 text-js-blue font-semibold">
                    Read Article <ArrowRight size={18} className="group-hover:translate-x-1.5 transition-transform duration-300" />
                  </span>
                </div>
              </Link>
            </Reveal>
          )}

          {/* CATEGORY FILTER */}
          <div className="flex flex-wrap gap-2.5 mb-10" data-testid="blog-filters">
            {BLOG_CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`blog-cat-${c.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setCat(c)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 ${
                  cat === c ? "bg-js-blue text-white" : "bg-js-grey text-js-charcoal/70 hover:bg-js-blue/10 hover:text-js-blue"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {/* GRID */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {list.map((a, i) => (
              <Reveal key={a.slug} delay={(i % 3) * 0.06}>
                <Link
                  to={`/blog/${a.slug}`}
                  data-testid="blog-card"
                  className="group flex flex-col bg-js-grey rounded-3xl overflow-hidden border border-black/5 h-full hover:shadow-[0_24px_55px_rgba(0,0,0,0.1)] hover:-translate-y-1.5 transition-all duration-400"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-js-blue text-xs font-semibold">{a.category}</span>
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <h3 className="font-display font-bold text-lg leading-snug flex items-start gap-1.5 group-hover:text-js-blue transition-colors duration-300">
                      {a.title}
                      <ArrowUpRight size={18} className="text-js-blue shrink-0 mt-1 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                    </h3>
                    <p className="text-js-charcoal/60 text-sm mt-3 leading-relaxed flex-1">{a.excerpt}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-js-blue text-sm font-semibold">
                      Read Article <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
};

export default Blog;
